import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { FilterService } from '../../../../../_shared/services';
import { calculateChartYTicks } from '../../../../../_shared/helpers/chart-y-ticks.helper';
import { DateInterval } from '../../../../../_shared/enums';
import { ExecutiveStatsRequest, ExecutiveStatsSummaryRequest } from '../../../../../_shared/models/driver-summary-request.model';
import { ExecutiveStatsService } from '../../../services/executive-stats.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { GroupedBarChartComponent } from '../../../../../_shared/components/graphs/grouped-bar-chart/grouped-bar-chart.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { GroupedChartData, TimeSeriesGroupingHelper } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { TripsModalComponent } from '../../modals/trips-modal/trips-modal.component';
import { GraphErrorStateComponent } from '../../../../../_shared/components/graphs/graph-error-state/graph-error-state.component';
import { differenceInDays } from 'date-fns';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-trips-widget',
  standalone: true,
  imports: [CommonModule, GroupedBarChartComponent, GraphTrendIconComponent, EmptyStateComponent, GraphErrorStateComponent, TranslocoDirective],
  templateUrl: './trips-widget.component.html',
  styleUrl: './trips-widget.component.scss'
})
export class TripsWidgetComponent extends BaseWidgetComponent {
  private modalService = inject(NgbModal);
  private executiveStatsService = inject(ExecutiveStatsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  public hasError: boolean = false;
  public errorMessage: string = '';
  public hasResult: boolean = false;
  public chartData: GroupedChartData[] = [];
  readonly barWidth = 12;
  readonly chartHeight = 175;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;

  readonly allSubgroups = [
    { name: 'Poor', legend: this.transloco.translate('executiveStats.chart.poor'), color: '#FF7F00' },
    { name: 'Fair', legend: this.transloco.translate('executiveStats.chart.fair'), color: '#F4BC2B' },
    { name: 'Good', legend: this.transloco.translate('executiveStats.chart.good'), color: '#50AD41' },
    { name: 'Globaltrends', legend: this.transloco.translate('executiveStats.chart.globalTrends'), color: '#3873C9' }
  ];

  // Dynamic subgroups based on which have data
  subgroups: Array<{ name: string, legend: string, color: string }> = [];

  public thresholds = {
    day: { floor: 2, ceiling: 4 },
    weekly: { floor: 10, ceiling: 20 },
    monthly: { floor: 40, ceiling: 80 }
  };

  constructor() {
    const filterService = inject(FilterService);
    super(filterService);
    this.isLoading = true;
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;

    try {
      const summaryRequest: ExecutiveStatsSummaryRequest = {
        driverIds: [...this.widgetFilter.driverIds],
        accountIds: [...this.widgetFilter.accountIds],
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      };

      const request: ExecutiveStatsRequest = {
        driverIds: [...this.widgetFilter.driverIds],
        accountIds: [...this.widgetFilter.accountIds],
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval
      };

      this.cancelSubject.next();
      const [trips, tripInsights, globalTrips] = await Promise.all([
        this.executiveStatsService.getTrips(request, this.cancelSubject),
        this.executiveStatsService.getTripInsightsSummary(summaryRequest, this.cancelSubject),
        this.executiveStatsService.getGlobalTrips(request, this.cancelSubject)
      ]);

      this.value = tripInsights.data?.totalValue ?? 0;
      this.percentageDifference = tripInsights.data?.percentageDifference ?? 0;

      const tripsData = trips.data ?? [];
      const globalTripsData = globalTrips.data ?? [];

      this.chartData = this.processResponses(tripsData, globalTripsData);
      this.yTicks = this.calculateYTicks(this.chartData);

      // Update subgroups to only include those with data
      this.updateActiveSubgroups();

      this.hasResult = this.chartData.some(point =>
        this.allSubgroups.some(s => (Number(point[s.name]) || 0) > 0)
      );

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.hasError = true;
          this.errorMessage = message;
          this.isLoading = false;
        }
      });
    }
  }

  private getThresholdForInterval(): { floor: number; ceiling: number } {
    if (this.widgetFilter.interval === DateInterval.Week) {
      return this.thresholds.weekly;
    } else if (this.widgetFilter.interval === DateInterval.Day || this.widgetFilter.isCustomDate) {
      return this.thresholds.day;
    }
    return this.thresholds.monthly;
  }

  private processResponses(tripsData: any[], globalTripsData: any[]): GroupedChartData[] {
    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);
    const groupingType = this.determineGroupingType(daysDifference, this.widgetFilter.interval);

    // First group the data by time period
    const datasets = [
      { data: tripsData, label: 'You' },
      { data: globalTripsData, label: 'Globaltrends' }
    ];

    let groupedData: GroupedChartData[];
    switch (groupingType) {
      case 'hourly':
        groupedData = TimeSeriesGroupingHelper.groupByHours(datasets);
        break;
      case 'daily':
        groupedData = TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
        break;
      case 'monthly':
        groupedData = TimeSeriesGroupingHelper.groupByMonths(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
        break;
      default:
        groupedData = TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
        break;
    }

    // Apply threshold-based coloring
    const threshold = this.getThresholdForInterval();
    return groupedData.map(item => {
      const youVal = Number(item['You']) || 0;
      const globalVal = Number(item['Globaltrends']) || 0;

      return {
        ...item,
        Poor: youVal < threshold.floor ? youVal : 0,
        Fair: youVal >= threshold.floor && youVal <= threshold.ceiling ? youVal : 0,
        Good: youVal > threshold.ceiling ? youVal : 0,
        Globaltrends: globalVal
      } as GroupedChartData;
    });
  }

  private determineGroupingType(daysDifference: number, interval: DateInterval): 'hourly' | 'daily' | 'weekly' | 'monthly' {
    if (daysDifference === 0) return 'hourly';
    if (daysDifference <= 7) return 'daily';
    if (daysDifference > 60) return 'monthly';
    return 'weekly';
  }

  private calculateYTicks(chartData: GroupedChartData[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const allValues = chartData.flatMap(item => [
      Number(item['Poor']) || 0,
      Number(item['Fair']) || 0,
      Number(item['Good']) || 0,
      Number(item['Globaltrends']) || 0
    ]);

    return calculateChartYTicks(allValues, this.defaultYTicks);
  }

  public override formatValue(value: number): string {
    return `${value.toLocaleString()}`;
  }

  public formatDifference(value: number): string {
    return `${value.toLocaleString('default', { maximumFractionDigits: 2 })}%`;
  }

  formatPercentChange(): string {
    const sign = this.percentageDifference > 0 ? '+' : '';
    return `${sign}${this.percentageDifference.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  formatTotalValue(): string {
    return `${this.value.toLocaleString()}`;
  }

  isUptrendPositive(): boolean {
    return true;
  }

  hasData(): boolean {
    return this.hasResult && this.chartData.length > 0;
  }

  private updateActiveSubgroups(): void {
    const activeNames = new Set<string>();
    this.chartData.forEach(item => {
      this.allSubgroups.forEach(sg => {
        if ((Number(item[sg.name]) || 0) > 0) {
          activeNames.add(sg.name);
        }
      });
    });
    this.subgroups = this.allSubgroups.filter(sg => activeNames.has(sg.name));
  }

  openModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };

    this.modalService.open(TripsModalComponent, modalOptions);
  }
}